import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, CheckCircle, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { toast.error('Please enter your email'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/auth/forgot-password`, { email: email.trim() });
      toast.success(data.message || 'If an account exists, a reset link was sent.');
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send reset email');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="bg-white border border-[#E4DFD3] rounded-lg shadow-sm p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-[#2C4A3B] rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-xl">🍄</span>
          </div>
          <h1 className="text-2xl font-extrabold text-[#1A2620] font-['Manrope']">Mushroom Farm</h1>
        </div>

        {sent ? (
          <div className="text-center space-y-3" data-testid="forgot-sent">
            <div className="w-14 h-14 bg-[#EDF5EE] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-[#3A7D44]" />
            </div>
            <h2 className="text-lg font-bold text-[#1A2620]">Check your inbox</h2>
            <p className="text-sm text-[#5C6B63]">If an account exists for <strong>{email}</strong>, we've sent a password-reset link. It expires in 1 hour.</p>
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#2C4A3B] hover:underline mt-2">
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-[#1A2620] mb-1">Forgot your password?</h2>
            <p className="text-sm text-[#5C6B63] mb-5">Enter the email tied to your account and we'll send you a reset link.</p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
              <div>
                <label className="block text-sm font-semibold text-[#1A2620] mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6B63]" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full rounded-md border border-[#E4DFD3] bg-white pl-9 pr-3 py-2.5 text-sm text-[#1A2620] placeholder-[#5C6B63] focus:border-[#2C4A3B] focus:outline-none focus:ring-1 focus:ring-[#2C4A3B]"
                    placeholder="you@example.com"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#2C4A3B] hover:bg-[#3E6652] text-white rounded-md px-4 py-2.5 font-medium text-sm disabled:opacity-50"
                data-testid="forgot-submit"
              >
                {submitting ? 'Sending…' : 'Send reset link'}
              </button>
            </form>

            <div className="mt-5 text-center">
              <Link to="/login" className="text-sm text-[#2C4A3B] hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" /> Back to login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
